import React from "react"
import { composeSx } from "../theme/composeSx"
import type { SvgIconProps, Theme } from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import MenuIcon from "@mui/icons-material/Menu"
import SearchIcon from "@mui/icons-material/Search"
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined"
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined"
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked"
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty"
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined"
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined"
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined"
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined"
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined"
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined"
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { colors } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type ViraIconName =
  | "check"
  | "close"
  | "chevronDown"
  | "chevronLeft"
  | "chevronRight"
  | "collapse"
  | "menu"
  | "search"
  | "mic"
  | "note"
  | "plan"
  | "sleep"
  | "activity"
  | "language"
  | "assessment"
  | "diary"
  | "calendar"
  | "relapse"
  | "settings"
  | "account"
  | "statusReady"
  | "statusMissing"
  | "statusPending"
  | "sun"
  | "moon"

const ICONS: Record<
  ViraIconName,
  React.ComponentType<SvgIconProps>
> = {
  check: CheckIcon,
  close: CloseIcon,
  chevronDown: KeyboardArrowDownIcon,
  chevronLeft: KeyboardArrowLeftIcon,
  chevronRight: KeyboardArrowRightIcon,
  collapse: ChevronLeftIcon,
  menu: MenuIcon,
  search: SearchIcon,
  mic: MicNoneOutlinedIcon,
  note: DescriptionOutlinedIcon,
  plan: AccountTreeOutlinedIcon,
  sleep: BedtimeOutlinedIcon,
  activity: DirectionsWalkOutlinedIcon,
  language: ChatBubbleOutlineOutlinedIcon,
  assessment: AssignmentOutlinedIcon,
  diary: EventNoteOutlinedIcon,
  calendar: CalendarMonthOutlinedIcon,
  relapse: HealthAndSafetyOutlinedIcon,
  settings: SettingsOutlinedIcon,
  account: PersonOutlineOutlinedIcon,
  statusReady: CheckCircleOutlineIcon,
  statusMissing: RadioButtonUncheckedIcon,
  statusPending: HourglassEmptyIcon,
  sun: LightModeOutlinedIcon,
  moon: DarkModeOutlinedIcon,
}

export type ViraIconProps = Omit<SvgIconProps, "color"> & {
  name: ViraIconName
  /** Defaults to Loop muted */
  tone?: "ink" | "muted" | "subtle" | "accent" | "positive" | "inherit"
}

function toneColor(
  theme: Theme,
  tone: NonNullable<ViraIconProps["tone"]>,
): string {
  const vira = resolveViraPalette(theme)
  switch (tone) {
    case "ink":
      return vira.ink
    case "muted":
      return vira.muted
    case "subtle":
      return vira.subtle
    case "accent":
      return vira.accent
    case "positive":
      return colors.graphs.alerts.positive
    case "inherit":
      return "inherit"
  }
}

export function ViraIcon({
  name,
  tone = "muted",
  sx,
  fontSize = "small",
  ...rest
}: ViraIconProps) {
  const Icon = ICONS[name]
  return (
    <Icon
      fontSize={fontSize}
      sx={composeSx((theme) => ({
        color: toneColor(theme, tone)}), sx)}
      {...rest}
    />
  )
}
